'use strict';

/**
 * Ops Console -- the platform-admin observability screen. One read (GET /1.0/Admin/Ops via
 * providers.OpsAPI) backs the whole page: grouped errors, who is active now, a recent activity
 * feed, and recent deploys. Template-driven per the Pict conventions (onBeforeRender derives the
 * render slots into AppData; templates iterate them; actions mutate AppData + render). No innerHTML.
 * See design/Observability-Plan.md.
 */

const libPictView = require('pict-view');

class PictViewOpsConsole extends libPictView
{
	_data() { return this.pict.AppData.Ops || {}; }
	_api() { return this.pict.providers.OpsAPI; }

	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpString = String(pValue);
		let tmpParsed = Date.parse(tmpString);
		if (!isNaN(tmpParsed)) { return tmpParsed; }
		tmpParsed = Date.parse(tmpString.replace(' ', 'T') + 'Z');
		return isNaN(tmpParsed) ? 0 : tmpParsed;
	}

	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSeconds = Math.floor((Date.now() - tmpMs) / 1000);
		if (tmpSeconds < 0) { tmpSeconds = 0; }
		if (tmpSeconds < 60) { return tmpSeconds + 's ago'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm ago'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		if (tmpHours < 24) { return tmpHours + 'h ago'; }
		return Math.floor(tmpHours / 24) + 'd ago';
	}

	onBeforeRender(pRenderable)
	{
		let tmpD = this._data();
		let tmpSummary = tmpD.Summary || {};

		tmpD.SummaryCards =
		[
			{ Label: 'Active now', Value: (tmpSummary.ActiveUsers != null) ? tmpSummary.ActiveUsers : 0, Note: 'last ' + (tmpSummary.ActiveWindowMinutes || 10) + ' min' },
			{ Label: 'Error groups', Value: (tmpSummary.ErrorGroups != null) ? tmpSummary.ErrorGroups : 0, Note: 'distinct' },
			{ Label: 'Errors scanned', Value: (tmpSummary.ErrorsScanned != null) ? tmpSummary.ErrorsScanned : 0, Note: 'recent window' }
		];

		tmpD.ErrorRows = (tmpD.Errors || []).map((pRow) => (
		{
			Count: pRow.Count || 1,
			ErrorType: pRow.ErrorType || 'Error',
			Message: String(pRow.Message || '').slice(0, 200),
			Route: pRow.Route || '',
			Method: pRow.Method || '',
			StatusCode: pRow.StatusCode || '',
			Ago: this._ago(pRow.LastSeen)
		}));
		tmpD.ErrorsEmpty = (tmpD.ErrorRows.length) ? [] : [1];

		tmpD.ActiveRows = (tmpD.ActiveUsers || []).map((pRow) => (
		{
			User: pRow.User || ('User ' + pRow.IDUser),
			Ago: this._ago(pRow.LastSeen)
		}));
		tmpD.ActiveEmpty = (tmpD.ActiveRows.length) ? [] : [1];

		tmpD.ActivityRows = (tmpD.Activity || []).map((pRow) => (
		{
			User: pRow.User || ('User ' + pRow.IDUser),
			Summary: (pRow.Action || 'did') + ' ' + (pRow.TargetType || '') + (pRow.IDTarget ? (' #' + pRow.IDTarget) : ''),
			Ago: this._ago(pRow.CreateDate)
		}));
		tmpD.ActivityEmpty = (tmpD.ActivityRows.length) ? [] : [1];

		tmpD.DeployRows = (tmpD.Deploys || []).map((pRow) => (
		{
			CommitHash: String(pRow.CommitHash || '').slice(0, 10),
			DeployedBy: pRow.DeployedBy || '',
			Result: pRow.Result || '',
			Environment: pRow.Environment || '',
			Ago: this._ago(pRow.CreateDate)
		}));
		tmpD.DeploysEmpty = (tmpD.DeployRows.length) ? [] : [1];

		tmpD.LoadErrorSlot = tmpD.LoadError ? [{ Message: tmpD.LoadError }] : [];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Entry point from the router: load then paint.
	show() { return this.refresh(); }

	refresh()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadOps !== 'function') { this.render(); return Promise.resolve(); }
		return tmpAPI.loadOps().then(() => this.render());
	}
}

module.exports = PictViewOpsConsole;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-OpsConsole',
	DefaultRenderable: 'Plansheet-OpsConsole',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-ops { padding: 20px 24px 60px; max-width: 1100px; }
		.ps-ops-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
		.ps-ops-head h1 { font-size: 22px; font-weight: 600; margin: 0; flex: 1; color: var(--theme-color-text-primary, #2c3140); }
		.ps-ops-refresh { padding: 7px 14px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-ops-refresh:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-ops-err { background: var(--theme-color-status-error, #cc3333); color: #fff; padding: 8px 12px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; }
		.ps-ops-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 22px; }
		.ps-ops-card { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0);
			border-radius: 12px; padding: 14px 16px; }
		.ps-ops-card-value { font-size: 28px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); line-height: 1.1; }
		.ps-ops-card-label { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin-top: 2px; }
		.ps-ops-card-note { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); margin-top: 1px; }
		.ps-ops-section { margin-bottom: 26px; }
		.ps-ops-section-title { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
			color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 8px; }
		.ps-ops-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 13px; padding: 8px 2px; }
		.ps-ops-errrow { display: flex; gap: 12px; align-items: baseline; padding: 9px 12px; border: 1px solid var(--theme-color-border-light, #e7ecf0);
			border-left: 3px solid var(--theme-color-status-error, #cc3333); border-radius: 8px; margin-bottom: 7px; background: var(--theme-color-background-panel, #fff); }
		.ps-ops-count { flex: 0 0 auto; min-width: 30px; text-align: center; font-weight: 700; font-size: 13px; color: var(--theme-color-status-error, #cc3333);
			background: color-mix(in srgb, var(--theme-color-status-error, #cc3333) 12%, transparent); border-radius: 6px; padding: 2px 6px; }
		.ps-ops-errbody { flex: 1; min-width: 0; }
		.ps-ops-errtype { font-weight: 600; font-size: 13px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-ops-errmsg { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-ops-errmeta { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-ops-when { flex: 0 0 auto; font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-ops-row { display: flex; gap: 10px; align-items: baseline; padding: 6px 10px; border-radius: 6px; font-size: 13px; }
		.ps-ops-row:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-ops-row-user { font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-ops-row-what { flex: 1; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-ops-dot { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; background: var(--theme-color-status-success, #3fae5a); align-self: center; }
		.ps-ops-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
		@media (max-width: 820px) { .ps-ops-cards { grid-template-columns: 1fr; } .ps-ops-cols { grid-template-columns: 1fr; } }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-OpsConsole',
			Template: /*html*/`
<div class="ps-ops">
	<div class="ps-ops-head">
		<h1>Ops Console</h1>
		<button class="ps-ops-refresh" onclick="{~P~}.views['Plansheet-OpsConsole'].refresh()">Refresh</button>
	</div>
	{~TS:Ops-LoadError:AppData.Ops.LoadErrorSlot~}
	<div class="ps-ops-cards">{~TS:Ops-Card:AppData.Ops.SummaryCards~}</div>

	<div class="ps-ops-section">
		<p class="ps-ops-section-title">Errors (grouped)</p>
		{~TS:Ops-ErrorsEmpty:AppData.Ops.ErrorsEmpty~}
		{~TS:Ops-ErrorRow:AppData.Ops.ErrorRows~}
	</div>

	<div class="ps-ops-cols">
		<div class="ps-ops-section">
			<p class="ps-ops-section-title">Active now</p>
			{~TS:Ops-ActiveEmpty:AppData.Ops.ActiveEmpty~}
			{~TS:Ops-ActiveRow:AppData.Ops.ActiveRows~}
		</div>
		<div class="ps-ops-section">
			<p class="ps-ops-section-title">Recent deploys</p>
			{~TS:Ops-DeploysEmpty:AppData.Ops.DeploysEmpty~}
			{~TS:Ops-DeployRow:AppData.Ops.DeployRows~}
		</div>
	</div>

	<div class="ps-ops-section">
		<p class="ps-ops-section-title">Recent activity</p>
		{~TS:Ops-ActivityEmpty:AppData.Ops.ActivityEmpty~}
		{~TS:Ops-ActivityRow:AppData.Ops.ActivityRows~}
	</div>
</div>`
		},
		{ Hash: 'Ops-LoadError', Template: /*html*/`<div class="ps-ops-err">Could not load the ops console: {~D:Record.Message~}</div>` },
		{ Hash: 'Ops-Card', Template: /*html*/`<div class="ps-ops-card"><div class="ps-ops-card-value">{~D:Record.Value~}</div><div class="ps-ops-card-label">{~D:Record.Label~}</div><div class="ps-ops-card-note">{~D:Record.Note~}</div></div>` },
		{ Hash: 'Ops-ErrorsEmpty', Template: /*html*/`<p class="ps-ops-empty">No errors recorded. (5xx server errors land here, grouped by where they happened.)</p>` },
		{
			Hash: 'Ops-ErrorRow',
			Template: /*html*/`<div class="ps-ops-errrow"><span class="ps-ops-count">{~D:Record.Count~}</span><div class="ps-ops-errbody"><div class="ps-ops-errtype">{~D:Record.ErrorType~}</div><div class="ps-ops-errmsg">{~D:Record.Message~}</div><div class="ps-ops-errmeta">{~D:Record.Method~} {~D:Record.Route~} &middot; {~D:Record.StatusCode~}</div></div><span class="ps-ops-when">{~D:Record.Ago~}</span></div>`
		},
		{ Hash: 'Ops-ActiveEmpty', Template: /*html*/`<p class="ps-ops-empty">No one active in the window.</p>` },
		{ Hash: 'Ops-ActiveRow', Template: /*html*/`<div class="ps-ops-row"><span class="ps-ops-dot"></span><span class="ps-ops-row-user">{~D:Record.User~}</span><span class="ps-ops-row-what"></span><span class="ps-ops-when">{~D:Record.Ago~}</span></div>` },
		{ Hash: 'Ops-DeploysEmpty', Template: /*html*/`<p class="ps-ops-empty">No deploys recorded yet.</p>` },
		{ Hash: 'Ops-DeployRow', Template: /*html*/`<div class="ps-ops-row"><span class="ps-ops-row-user">{~D:Record.CommitHash~}</span><span class="ps-ops-row-what">{~D:Record.Result~} {~D:Record.Environment~} &middot; {~D:Record.DeployedBy~}</span><span class="ps-ops-when">{~D:Record.Ago~}</span></div>` },
		{ Hash: 'Ops-ActivityEmpty', Template: /*html*/`<p class="ps-ops-empty">No activity recorded yet.</p>` },
		{ Hash: 'Ops-ActivityRow', Template: /*html*/`<div class="ps-ops-row"><span class="ps-ops-row-user">{~D:Record.User~}</span><span class="ps-ops-row-what">{~D:Record.Summary~}</span><span class="ps-ops-when">{~D:Record.Ago~}</span></div>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-OpsConsole',
			TemplateHash: 'Plansheet-OpsConsole',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
